/**
 * V2PricingPage — PRP-MCP-9: two plans, Pro and Pro Plus, each with the
 * assistant, export, and an included number of conversions per month, then
 * pay-as-you-go packs, then a plain-English "How credits work" explainer.
 * Every number comes from GET /billing/config (data in plans.json) so the UI
 * never hardcodes amounts that could drift from Stripe.
 *
 * Monthly is the default; the toggle shows the annual price (10 months' price).
 * One "Founding 20" ribbon shows the live seat count from
 * GET /api/public/founder-seats; it is hidden when no seats remain or the count
 * cannot be read. While seats remain each card also shows the founding number.
 *
 * PRP-MCP-8: below the plans, the $7 chat trial card. The website, Settings and
 * the locked export panel link to `/pricing?trial=1` (Settings adds
 * `#chat-trial`); either one scrolls to and highlights the card. A 409 (trial
 * already used) shows the server's message and points to the plans. Shown only
 * when the backend says the trial is available. Signed-out visitors go through
 * ProtectedRoute to sign-in like every plan button; `postLoginReturn` brings
 * `/pricing?trial=1` back after sign-in.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { BackendAPI } from '../../lib/api';
import { ChatTrialAPI } from '../../lib/chatTrialApi';
import { PlansAPI } from '../../lib/plansApi';
import type { BillingConfig, BillingInterval, BillingPlan, FounderSeats, PlanId } from '../../lib/plansApi';
import { clearReturnPath } from '../../lib/postLoginReturn';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Segmented } from '../components/ui/segmented';

/** cents → "$149" / "$1,490" (whole dollars). */
function dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

interface Props {
  /** Where to send the browser for Stripe Checkout. Injected for testability. */
  redirect?: (url: string) => void;
}

function Busy() {
  return (
    <span className="flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" /> Starting checkout…
    </span>
  );
}

/** Public pricing page (accessible to any logged-in user). */
export default function V2PricingPage({
  redirect = (url) => window.location.assign(url),
}: Props) {
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [seats, setSeats] = useState<FounderSeats | null>(null);
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trialRefused, setTrialRefused] = useState<string | null>(null);
  const { hash, search } = useLocation();
  const trialLinked =
    hash === '#chat-trial' || new URLSearchParams(search).get('trial') === '1';

  useEffect(() => {
    let cancelled = false;
    PlansAPI.getBillingConfig()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load pricing. Try again, or email support.');
      });
    // The seat count is optional: on any failure the ribbon simply stays hidden.
    PlansAPI.getFounderSeats()
      .then((s) => {
        if (!cancelled) setSeats(s);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function subscribe(plan: PlanId) {
    setBusy(`plan-${plan}`);
    setError(null);
    try {
      const { url } = await PlansAPI.createPlanSession(plan, interval);
      redirect(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
      setBusy(null);
    }
  }

  // Arriving on /pricing?trial=1 (or #chat-trial): the card renders after the
  // config loads, so scroll to it then (the browser's own anchor jump ran too early).
  useEffect(() => {
    if (config && trialLinked) {
      clearReturnPath(); // the sign-in detour (if any) has brought us back here
      document.getElementById('chat-trial')?.scrollIntoView?.({ block: 'center' });
    }
  }, [config, trialLinked]);

  async function startTrial() {
    setBusy('trial');
    setError(null);
    setTrialRefused(null);
    try {
      const { url } = await ChatTrialAPI.createTrialSession();
      redirect(url);
    } catch (e) {
      const err = e as { status?: number; message?: string } | null;
      if (err?.status === 409) {
        setTrialRefused(err.message || 'You have already had a trial or a subscription.');
      } else {
        setError(err?.message || 'Could not start the trial.');
      }
      setBusy(null);
    }
  }

  async function buyPack(packId: string) {
    setBusy(`pack-${packId}`);
    setError(null);
    try {
      const { url } = await BackendAPI.createCheckoutSession(packId);
      redirect(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout.');
      setBusy(null);
    }
  }

  if (!config) {
    return (
      <div className="px-6 py-10 mx-auto w-full max-w-[1080px]">
        <p className="text-[13px] text-[var(--tx-lo)]">{error ?? 'Loading pricing…'}</p>
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="px-6 py-10 mx-auto w-full max-w-[1080px]">
        <h1 className="text-[22px] font-[580] text-[var(--tx-hi)]">Pricing</h1>
        <p className="mt-2 text-[13px] text-[var(--tx-faint)]">
          Payments turn on shortly — check back soon.
        </p>
      </div>
    );
  }

  const { credits, plans, packs, founder } = config;
  const trial = config.chat_trial?.available ? config.chat_trial : null;
  const trialPlan: BillingPlan | undefined = trial
    ? plans.find((p) => p.id === trial.base_plan)
    : undefined;
  const isAnnual = interval === 'annual';
  const foundingOpen = !!seats && seats.remaining > 0;
  const perk = `your first ${founder.perk_months} months`;

  return (
    <div className="px-6 py-8 mx-auto w-full max-w-[1080px]">
      <header className="mb-6">
        <h1 className="text-[22px] font-[580] text-[var(--tx-hi)] tracking-[-0.01em]">Pricing</h1>
        <p className="mt-1 text-[13px] text-[var(--tx-lo)]">
          Every plan includes the assistant and export, plus conversions each month. A conversion
          turns one estimate PDF into a Xactimate-ready ESX. Need more? Add a pack.
        </p>
      </header>

      {error && (
        <p className="mb-4 text-[12.5px] font-medium text-[var(--bad)]">{error}</p>
      )}

      {/* ---------- Plans (primary) ---------- */}
      <section id="plans" className="mb-10 scroll-mt-6">
        <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-[15px] font-[580] text-[var(--tx-hi)]">Plans</h2>
          <div className="flex items-center gap-2">
            <Segmented<BillingInterval>
              value={interval}
              onValueChange={setInterval}
              options={[
                { value: 'month', label: 'Monthly' },
                { value: 'annual', label: 'Annual' },
              ]}
            />
            <span className="text-[11px] font-medium text-[var(--accent)]">
              Annual = 2 months free
            </span>
          </div>
        </div>

        {foundingOpen && seats && (
          <div
            className="mb-3 rounded-[var(--r-md)] border border-[var(--accent)] px-3 py-2 text-[12.5px] text-[var(--tx-hi)]"
            data-testid="founder-ribbon"
          >
            <span className="font-semibold text-[var(--accent)]">Founding 20</span>
            {` · ${seats.remaining} of ${seats.total} left. Subscribe now and get the founding number of conversions every month for ${perk}.`}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {plans.map((p) => {
            const price = isAnnual ? p.annual : p.monthly;
            return (
              <Card key={p.id} className="p-4 flex flex-col" data-testid={`plan-${p.id}`}>
                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--tx-lo)]">
                  {p.name}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-[24px] font-[640] text-[var(--tx-hi)]">
                    {dollars(price.amount_cents)}
                  </span>
                  <span className="text-[12px] text-[var(--tx-lo)]">
                    {isAnnual ? '/year' : '/month'}
                  </span>
                </div>
                <ul className="mt-3 space-y-1 text-[12.5px] text-[var(--tx-mid)]">
                  <li>The assistant (chat)</li>
                  <li>Export to Xactimate</li>
                  <li data-testid={`plan-${p.id}-included`}>
                    {`${p.included_conversions} conversions included each month`}
                  </li>
                  {foundingOpen && (
                    <li className="text-[var(--accent)]" data-testid={`plan-${p.id}-founding`}>
                      {`Founding 20: ${p.founder_conversions} a month for ${perk}`}
                    </li>
                  )}
                  <li className="text-[var(--tx-lo)]">Need more? Packs, which never expire</li>
                </ul>
                <div className="flex-1" />
                <Button
                  variant="primary"
                  className="mt-4 w-full justify-center"
                  disabled={busy !== null}
                  onClick={() => subscribe(p.id)}
                  data-testid={`subscribe-${p.id}`}
                >
                  {busy === `plan-${p.id}` ? <Busy /> : `Subscribe to ${p.name}`}
                </Button>
              </Card>
            );
          })}
        </div>
        <p className="mt-3 text-[12px] text-[var(--tx-lo)]">
          Included conversions reset each month and do not carry over. Export is part of the
          plan. There is no per-estimate charge.
        </p>
      </section>

      {/* ---------- $7 chat trial (PRP-MCP-8, converts to Pro since PRP-MCP-9) ---------- */}
      {trial && (
        <section id="chat-trial" className="mb-10 scroll-mt-6" data-testid="chat-trial">
          <Card
            className={`p-4 flex items-center justify-between gap-4 flex-wrap${
              trialLinked ? ' ring-2 ring-[var(--accent)]' : ''
            }`}
            data-testid="chat-trial-card"
            data-highlighted={trialLinked ? 'true' : 'false'}
          >
            <div className="min-w-0">
              <div className="text-[13px] font-[560] text-[var(--tx-hi)]">
                {`${trial.trial_days}-day trial, ${dollars(trial.fee_cents)}`}
              </div>
              <div className="mt-0.5 text-[12px] text-[var(--tx-lo)]">
                {`${dollars(trial.fee_cents)} for ${trial.trial_days} days, everything included, export included, ${trial.included_conversions} conversions. `}
                {trialPlan
                  ? `Converts to ${trialPlan.name} at ${dollars(trialPlan.monthly.amount_cents)}/month unless you cancel within ${trial.trial_days} days.`
                  : `Converts to a monthly plan unless you cancel within ${trial.trial_days} days.`}
              </div>
              <div className="mt-0.5 text-[12px] text-[var(--tx-lo)]">
                Export is part of a subscription. There is no per-estimate charge.
              </div>
              {trialRefused && (
                <p
                  className="mt-2 mb-0 text-[12.5px] font-medium text-[var(--bad)]"
                  data-testid="trial-refused"
                >
                  {trialRefused}{' '}
                  <a href="#plans" className="text-[var(--accent)] underline">
                    See the plans
                  </a>
                </p>
              )}
            </div>
            <Button
              variant="primary"
              disabled={busy !== null}
              onClick={startTrial}
              data-testid="start-trial"
            >
              {busy === 'trial' ? <Busy /> : `Start the ${dollars(trial.fee_cents)} trial`}
            </Button>
          </Card>
        </section>
      )}

      {/* ---------- Pay as you go (secondary) ---------- */}
      <section className="mb-10">
        <h2 className="mb-1 text-[15px] font-[580] text-[var(--tx-hi)]">
          Just need a few? Pay as you go.
        </h2>
        <p className="mb-4 text-[12.5px] text-[var(--tx-lo)]">
          No subscription. Buy credits when you need them — they never expire.
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          {packs.map((p) => {
            const noun = p.credits === 1 ? 'credit' : 'credits';
            return (
              <Button
                key={p.id}
                variant="secondary"
                className="w-full justify-center"
                disabled={busy !== null}
                onClick={() => buyPack(p.id)}
                data-testid={`pack-${p.id}`}
              >
                {busy === `pack-${p.id}` ? <Busy /> : `${p.credits} ${noun} — ${dollars(p.amount_cents)}`}
              </Button>
            );
          })}
        </div>
      </section>

      {/* ---------- How credits work ---------- */}
      <section className="mb-10">
        <h2 className="mb-3 text-[15px] font-[580] text-[var(--tx-hi)]">How credits work</h2>
        <Card className="p-4">
          <ul className="space-y-2 text-[12.5px] text-[var(--tx-mid)]">
            <li>
              {credits.free_line_threshold} line items or fewer ={' '}
              <span className="text-[var(--tx-hi)]">
                {credits.per_conversion} {credits.per_conversion === 1 ? 'credit' : 'credits'}
              </span>
              .
            </li>
            <li>
              More than {credits.free_line_threshold} line items ={' '}
              <span className="text-[var(--tx-hi)]">{credits.per_conversion_over} credits</span>.
            </li>
            <li>
              {credits.sketch_multiplier <= 1 ? (
                <>
                  Adding a sketch is{' '}
                  <span className="text-[var(--tx-hi)]">free</span>.
                </>
              ) : (
                <>
                  Adding a sketch multiplies the cost by{' '}
                  <span className="text-[var(--tx-hi)]">{credits.sketch_multiplier}</span>.
                </>
              )}
            </li>
            <li>A plan&apos;s included conversions are used first, then pack credits.</li>
            <li className="text-[var(--tx-lo)]">
              {`Your first ${credits.trial_signup_credits} conversions are free.`}
            </li>
          </ul>
        </Card>
      </section>

      {/* ---------- Orgs ---------- */}
      <section>
        <Card className="p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[13px] font-[560] text-[var(--tx-hi)]">Estimating for a team?</div>
            <div className="text-[12px] text-[var(--tx-lo)]">
              We build custom integrations and onboarding for restoration orgs.
            </div>
          </div>
          <Button variant="secondary" size="sm" asChild>
            <Link to="/partners" data-testid="partners-link">
              For organizations
            </Link>
          </Button>
        </Card>
      </section>
    </div>
  );
}
